import React, { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import dayjs from "dayjs";
import { ReactComponent as RatesIcon } from "../../../../assets/icons/dashboard/Rates.svg";
import { firestore } from "../../../../backend/firebaseconfig";

const tenors = ["1Y", "2Y", "3Y", "4Y", "5Y", "10Y"];
const columns = ["yesterday", "oneWeekAgo", "oneMonthAgo", "oneYearAgo"];
const percent = (value) =>
  typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(2)}%` : "—";
const dateLabel = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("DD MMMM YYYY") : "Unavailable";

export default function RatesMonitor({ server }) {
  const [bank, setBank] = useState(null);
  const [bankStatus, setBankStatus] = useState("loading");
  const [report, setReport] = useState(null);
  const [reportStatus, setReportStatus] = useState("loading");
  const observationDate = report?.asOfDate;

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    let active = true;
    setBankStatus("loading");
    fetch(`${server.replace(/\/$/, "")}/api/bank-of-england-rate`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Bank Rate request failed");
        return response.json();
      })
      .then((data) => {
        if (typeof data.rate !== "number" || !Number.isFinite(data.rate)) throw new Error("Invalid Bank Rate");
        if (active) { setBank(data); setBankStatus("ready"); }
      })
      .catch(() => { if (active) { setBank(null); setBankStatus("error"); } })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [server]);

  useEffect(() => onSnapshot(
    query(collection(firestore, "Compliance", "Swap Rate", "Reports"), orderBy("reportDate", "desc"), limit(1)),
    (snapshot) => {
      setReport(snapshot.docs[0]?.data() || null);
      setReportStatus(snapshot.empty ? "empty" : "ready");
    },
    () => { setReport(null); setReportStatus("error"); },
  ), []);

  return (
    <section className="dashboard-rates-monitor" aria-label="Rates Monitor">
      <div className="row-space-between flex-wrap gap-s">
        <div className="dashboard-announcements-main-header FP_Body_Semibold primary">
          <RatesIcon className="dashboard-announcements-main-icon" aria-hidden="true" /> Rates Monitor
        </div>
      </div>
      <div className="dashboard-announcement-card dashboard-bank-rate-summary p-xs gap-s">
        <div className="fit-column gap-xs"><div className="FP_Help_Text primary">Bank of England Rate</div>
          <div className="FP_Body_Semibold" aria-live="polite">{bankStatus === "loading" ? "Loading…" : percent(bank?.rate)}</div>
        </div>
        <div><div className="FP_Help_Text primary">As of</div><div className="FP_Help_Text">{bank ? dateLabel(bank.asOf) : "—"}</div></div>
        <div><div className="FP_Help_Text primary">Next BoE Decision</div><div className="FP_Help_Text">{dateLabel(bank?.nextDecisionDate)}</div></div>
      </div>
      {bankStatus === "error" && <p className="FP_Help_Text" role="status">Bank Rate is currently unavailable.</p>}
      <div className="dashboard-announcement-card dashboard-announcement-body">
        <div className="FP_Help_Text primary">SONIA Swap Rates</div>
        <div className="FP_Help_Text grey">Sterling Overnight Index Average</div>
        <div className="FP_Help_Text grey">
          {observationDate && dayjs(observationDate).isValid()
            ? `As of ${dayjs(observationDate).format("D MMMM YYYY")}, close of business London. `
            : ""}
          Indicative mid rates.
        </div>
        {reportStatus !== "ready" ? (
          <p className="FP_Help_Text" role="status">{reportStatus === "loading" ? "Loading swap rates…" : reportStatus === "empty" ? "No swap rate reports available." : "Swap rates are currently unavailable."}</p>
        ) : (
          <div className="table-scroll-container">
            <table className="FP_Help_Text">
              <caption hidden>SONIA swap rates, report dated {dateLabel(report.reportDate)}. All values are percentages.</caption>
              <thead><tr><th className="FP_Help_Text_Semibold" scope="col">Tenor</th><th className="FP_Help_Text_Semibold" scope="col" title="Previous working day in England and Wales, before the report date">{observationDate ? dayjs(observationDate).format("DD MMM YYYY") : "Previous working day"}</th><th className="FP_Help_Text_Semibold" scope="col">1w ago</th><th className="FP_Help_Text_Semibold" scope="col">1m ago</th><th className="FP_Help_Text_Semibold" scope="col">1y ago</th></tr></thead>
              <tbody>{tenors.map((tenor) => <tr key={tenor}><th scope="row" className="FP_Help_Text primary">{parseInt(tenor, 10)} Year</th>{columns.map((column) => <td key={column} className={column === "yesterday" ? "FP_Help_Text_Semibold" : "FP_Help_Text"}>{percent(report.rates?.[tenor]?.[column])}</td>)}</tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
